<template>
  <Modal
    v-model="visible"
    :title="computedTitle"
    :width="width"
    :mask-closable="false"
    class="robot-selector-modal"
  >
    <div class="robot-selector">
      <!-- 搜索栏 -->
      <div class="search-bar">
        <Input
          v-model="searchKeyword"
          :placeholder="computedSearchPlaceholder"
          prefix="ios-search"
          class="search-input"
          @on-input="handleSearch"
          @on-change="handleSearch"
        />
        <div class="selected-count">
          {{$translate('selectedCount')}}: {{ selectedRobotIds.length }} / {{ filteredRobotList.length }}
        </div>
      </div>

      <!-- 全选操作 -->
      <div class="select-all-bar">
        <Checkbox
          :value="isAllSelected"
          :indeterminate="isIndeterminate"
          @on-change="handleSelectAll"
          class="select-all-checkbox"
        >
          {{$translate('selectAll')}}
        </Checkbox>
        <Button
          type="text"
          @click="clearSelection"
          :disabled="selectedRobotIds.length === 0"
          class="clear-btn"
        >
          {{$translate('clearSelection')}}
        </Button>
      </div>

      <!-- 机器人列表 -->
      <div class="robot-list">
        <div v-if="loading" class="loading-container">
          <Spin size="large">
            <div class="loading-text">{{$translate('loadingRobots')}}</div>
          </Spin>
        </div>

        <div v-else-if="filteredRobotList.length === 0" class="empty-container">
          <div class="empty-icon">
            <svg-icon name="empty" class="empty-svg"></svg-icon>
          </div>
          <div class="empty-text">
            {{ searchKeyword ? $translate('noMatchingRobots') : $translate('noRobotData') }}
          </div>
        </div>

        <div v-else class="robot-grid">
          <div
            v-for="robot in filteredRobotList"
            :key="robot.id"
            class="robot-item"
            :class="{ 'selected': selectedRobotIds.includes(robot.id) }"
            @click="toggleRobot(robot)"
          >
            <div class="robot-checkbox" @click.stop>
              <Checkbox
                :value="selectedRobotIds.includes(robot.id)"
                @on-change="(checked) => handleCheckboxChange(robot, checked)"
                class="item-checkbox"
              />
            </div>

            <div class="robot-info">
              <div class="robot-avatar">
                <img :src="robotIcon" alt="robot" class="robot-img">
              </div>

              <div class="robot-details">
                <div class="robot-name">{{ robot.name }}</div>
                <div class="robot-code">{{ robot.code }}</div>
              </div>
            </div>


          </div>
        </div>
      </div>
    </div>

    <div slot="footer">
      <Button
        type="primary"
        @click="handleConfirm"
        class="confirm-btn"
      >
        {{$translate('confirmSelection')}} ({{ selectedRobotIds.length }})
      </Button>
      <Button @click="handleCancel" class="cancel-btn">{{$translate('cancel')}}</Button>
    </div>
  </Modal>
</template>

<script>
import api from '@/api'

export default {
  name: 'RobotSelector',
  props: {
    // 控制显示隐藏
    value: {
      type: Boolean,
      default: false
    },
    // 已选择的机器人ID列表
    selectedIds: {
      type: Array,
      default: () => []
    },
    // 弹窗标题
    title: {
      type: String,
      default: ''
    },
    // 弹窗宽度
    width: {
      type: Number,
      default: 800
    },
    // 搜索框占位符
    searchPlaceholder: {
      type: String,
      default: ''
    },
    // 机器人图标
    robotIcon: {
      type: String,
      default: require('@/assets/images/voice/Robot-icon.png')
    },
    // 是否多选
    multiple: {
      type: Boolean,
      default: true
    },
    // 自定义API接口
    apiUrl: {
      type: String,
      default: 'robot-server/robot/list/all'
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      searchKeyword: '',
      robotList: [],
      selectedRobotIds: []
    }
  },
  computed: {
    // 计算标题，如果没有传入则使用默认翻译
    computedTitle() {
      return this.title || this.$translate('selectRobot')
    },
    // 计算搜索占位符，如果没有传入则使用默认翻译
    computedSearchPlaceholder() {
      return this.searchPlaceholder || this.$translate('searchRobotPlaceholder')
    },
    filteredRobotList() {
      if (!this.searchKeyword.trim()) {
        return this.robotList
      }
      const keyword = this.searchKeyword.toLowerCase().trim()
      return this.robotList.filter(robot => {
        const name = (robot.name || '').toLowerCase()
        const code = (robot.code || '').toLowerCase()
        return name.includes(keyword) || code.includes(keyword)
      })
    },
    isAllSelected() {
      return this.filteredRobotList.length > 0 &&
             this.filteredRobotList.every(robot => this.selectedRobotIds.includes(robot.id))
    },
    isIndeterminate() {
      const selectedCount = this.filteredRobotList.filter(robot =>
        this.selectedRobotIds.includes(robot.id)
      ).length
      return selectedCount > 0 && selectedCount < this.filteredRobotList.length
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.visible = newVal
        if (newVal) {
          this.selectedRobotIds = [...this.selectedIds]
          this.loadRobotList()
        }
      },
      immediate: true
    },
    visible(newVal) {
      this.$emit('input', newVal)
    }
  },
  methods: {
    // 加载机器人列表
    loadRobotList() {
      this.loading = true

      api.getData(this.apiUrl).then(response => {
        this.loading = false
        if (response.state === 0) {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: response.msg || this.$translate('getRobotListFailed')
          })
        } else {
          this.robotList = response.data || []
        }
      })
    },

    // 搜索处理 - 实时搜索
    handleSearch() {
      // 由于使用computed属性filteredRobotList，搜索是实时的
      // 这里可以添加防抖逻辑如果需要
      this.$nextTick(() => {
        // 搜索后重新计算选中状态
        this.$forceUpdate()
      })
    },

    // 处理复选框变化
    handleCheckboxChange(robot, checked) {
      if (!this.multiple) {
        // 单选模式
        this.$set(this, 'selectedRobotIds', checked ? [robot.id] : [])
        return
      }

      // 多选模式
      const newSelectedIds = [...this.selectedRobotIds]
      if (checked) {
        if (!newSelectedIds.includes(robot.id)) {
          newSelectedIds.push(robot.id)
        }
      } else {
        const index = newSelectedIds.indexOf(robot.id)
        if (index > -1) {
          newSelectedIds.splice(index, 1)
        }
      }
      this.$set(this, 'selectedRobotIds', newSelectedIds)
    },

    // 切换机器人选择状态（点击卡片时）
    toggleRobot(robot) {
      if (!this.multiple) {
        // 单选模式
        const newSelectedIds = this.selectedRobotIds.includes(robot.id) ? [] : [robot.id]
        this.$set(this, 'selectedRobotIds', newSelectedIds)
        return
      }

      // 多选模式
      const newSelectedIds = [...this.selectedRobotIds]
      const index = newSelectedIds.indexOf(robot.id)
      if (index > -1) {
        newSelectedIds.splice(index, 1)
      } else {
        newSelectedIds.push(robot.id)
      }
      this.$set(this, 'selectedRobotIds', newSelectedIds)
    },

    // 全选/取消全选
    handleSelectAll(checked) {
      if (!this.multiple) return

      const newSelectedIds = [...this.selectedRobotIds]

      if (checked) {
        // 全选当前筛选的机器人
        this.filteredRobotList.forEach(robot => {
          if (!newSelectedIds.includes(robot.id)) {
            newSelectedIds.push(robot.id)
          }
        })
      } else {
        // 取消选择当前筛选的机器人
        this.filteredRobotList.forEach(robot => {
          const index = newSelectedIds.indexOf(robot.id)
          if (index > -1) {
            newSelectedIds.splice(index, 1)
          }
        })
      }

      this.$set(this, 'selectedRobotIds', newSelectedIds)
    },

    // 清空选择
    clearSelection() {
      this.$set(this, 'selectedRobotIds', [])
    },

    // 确认选择
    handleConfirm() {
      const selectedRobots = this.robotList.filter(robot =>
        this.selectedRobotIds.includes(robot.id)
      )
      this.$emit('confirm', selectedRobots)
      this.visible = false
    },

    // 取消选择
    handleCancel() {
      this.visible = false
    }
  }
}
</script>

<style lang="less" scoped>
.robot-selector {
  max-height: 600px;
  display: flex;
  flex-direction: column;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
}

.search-input {
  flex: 1;
}

.selected-count {
  font-size: 14px;
  color: #e8f4fd;
  font-weight: 500;
  white-space: nowrap;
  padding: 4px 12px;
  background: rgba(0, 191, 255, 0.1);
  border: 1px solid rgba(0, 191, 255, 0.3);
  border-radius: 12px;
}

.select-all-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e8eaec;
  margin-bottom: 20px;
}

.select-all-checkbox {
  font-weight: 500;
  color: #e8f4fd;
}

.clear-btn {
  color: #ff6b7a;
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 4px;
  transition: all 0.3s ease;
  background: rgba(255, 107, 122, 0.1);
  border: 1px solid rgba(255, 107, 122, 0.2);
}

.clear-btn:hover {
  color: #ffffff;
  background: rgba(255, 107, 122, 0.8);
  border-color: rgba(255, 107, 122, 0.8);
  box-shadow: 0 0 10px rgba(255, 107, 122, 0.3);
}

.robot-list {
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.loading-text {
  margin-top: 10px;
  color: #e8f4fd;
  font-size: 14px;
}

.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: rgba(232, 244, 253, 0.6);
}

.empty-icon {
  margin-bottom: 15px;
}

.empty-svg {
  font-size: 48px;
  color: rgba(0, 191, 255, 0.3);
}

.empty-text {
  font-size: 16px;
  color: rgba(232, 244, 253, 0.6);
}

.robot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 15px;
}

.robot-item {
  position: relative;
  display: flex;
  align-items: center;
  padding: 15px;
  border: 1px solid rgba(0, 191, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(15, 35, 70, 0.6);
  backdrop-filter: blur(5px);
}

.robot-item:hover {
  border-color: rgba(0, 191, 255, 0.6);
  box-shadow: 0 0 15px rgba(0, 191, 255, 0.2);
  background: rgba(15, 35, 70, 0.8);
}

.robot-item.selected {
  border-color: rgba(0, 191, 255, 0.8);
  background: rgba(0, 191, 255, 0.1);
  box-shadow: 0 0 15px rgba(0, 191, 255, 0.25);
}

.robot-checkbox {
  margin-right: 15px;
}

.robot-info {
  display: flex;
  align-items: center;
  gap: 15px;
  flex: 1;
}

.robot-avatar {
  position: relative;
  width: 48px;
  height: 48px;
}

.robot-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.robot-details {
  flex: 1;
}

.robot-name {
  font-size: 16px;
  font-weight: 500;
  color: #e8f4fd;
  margin-bottom: 4px;
}

.robot-code {
  font-size: 14px;
  color: #ffca00;
  margin-bottom: 4px;
}



.confirm-btn {
  background: linear-gradient(135deg, #00bfff, #0080ff);
  border: none;
  margin-right: 10px;
}

.cancel-btn {
  margin-left: 10px;
}

/* 滚动条样式 */
.robot-list::-webkit-scrollbar {
  width: 8px;
}

.robot-list::-webkit-scrollbar-track {
  background: rgba(15, 35, 70, 0.3);
  border-radius: 4px;
  border: 1px solid rgba(0, 191, 255, 0.1);
}

.robot-list::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, rgba(0, 191, 255, 0.6), rgba(0, 191, 255, 0.4));
  border-radius: 4px;
  border: 1px solid rgba(0, 191, 255, 0.2);
  transition: all 0.3s ease;
  box-shadow: 0 0 5px rgba(0, 191, 255, 0.2);
}

.robot-list::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, rgba(0, 191, 255, 0.8), rgba(0, 191, 255, 0.6));
  box-shadow: 0 0 10px rgba(0, 191, 255, 0.4);
}
</style>

<style lang="less">
.robot-selector-modal {
  .ivu-modal-header {
    background: linear-gradient(135deg, #00bfff, #0080ff);
    color: #fff;
    border-bottom: none;
  }

  .ivu-modal-header-inner {
    color: #fff;
    font-weight: 600;
  }

  .ivu-modal-close {
    color: #fff;
  }

  .ivu-modal-close:hover {
    color: rgba(255, 255, 255, 0.8);
  }

  .ivu-modal-body {
    background: linear-gradient(135deg,
      rgba(15, 30, 60, 0.95) 0%,
      rgba(25, 45, 80, 0.9) 50%,
      rgba(10, 25, 50, 0.95) 100%);
    color: #e8f4fd;
    position: relative;
  }

  .ivu-modal-body::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 30%, rgba(0, 191, 255, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(0, 191, 255, 0.06) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }

  .ivu-modal-body > * {
    position: relative;
    z-index: 1;
  }

  .ivu-modal-footer {
    background: linear-gradient(135deg,
      rgba(15, 30, 60, 0.95) 0%,
      rgba(25, 45, 80, 0.9) 50%,
      rgba(10, 25, 50, 0.95) 100%);
    border-top: 1px solid rgba(0, 191, 255, 0.3);
  }

  // 搜索输入框样式
  .ivu-input {
    background: rgba(15, 35, 70, 0.7) !important;
    border: 1px solid rgba(0, 191, 255, 0.4) !important;
    color: #e8f4fd !important;
    box-shadow: 0 0 5px rgba(0, 191, 255, 0.1) !important;
  }

  .ivu-input:focus {
    border-color: rgba(0, 191, 255, 0.8) !important;
    box-shadow: 0 0 15px rgba(0, 191, 255, 0.3) !important;
  }

  .ivu-input:hover {
    border-color: rgba(0, 191, 255, 0.6) !important;
    box-shadow: 0 0 8px rgba(0, 191, 255, 0.15) !important;
  }

  // 复选框样式统一
  .ivu-checkbox-wrapper {
    color: #e8f4fd !important;
  }

  .ivu-checkbox-inner {
    background: rgba(15, 35, 70, 0.7) !important;
    border: 2px solid rgba(0, 191, 255, 0.4) !important;
    transition: all 0.3s ease !important;
    width: 18px !important;
    height: 18px !important;
  }

  .ivu-checkbox-inner:hover {
    border-color: rgba(0, 191, 255, 0.6) !important;
    box-shadow: 0 0 8px rgba(0, 191, 255, 0.2) !important;
  }

  .ivu-checkbox-checked .ivu-checkbox-inner {
    background: linear-gradient(135deg, #00bfff, #0080ff) !important;
    border-color: #00bfff !important;
    box-shadow: 0 0 15px rgba(0, 191, 255, 0.4) !important;
  }

  .ivu-checkbox-checked .ivu-checkbox-inner::after {
    border-color: #ffffff !important;
    border-width: 2px !important;
    width: 6px !important;
    height: 10px !important;
  }

  .ivu-checkbox {
    margin-right: 0 !important;
  }

  // 选择栏样式
  .select-all-bar {
    border-bottom: 1px solid rgba(0, 191, 255, 0.2) !important;
  }

  // 空状态文字颜色
  .empty-container {
    color: rgba(232, 244, 253, 0.6) !important;
  }

  .empty-svg {
    color: rgba(0, 191, 255, 0.3) !important;
  }

  // 加载文字颜色
  .loading-text {
    color: #00bfff !important;
  }
}
</style>
